import type { ReactNode, CSSProperties } from "react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "./ui/utils";

/** Section title gradient (light backgrounds) */
export const HEADLINE_GRADIENT =
  "linear-gradient(90deg,var(--foreground) 0,var(--foreground) 33.33%,#53A4CF 40%,#DA5B9B 45%,#F3D573 50%,#000000 55%,#53A4CF 60%,transparent 66.67%,transparent)";

/** Same sweep shape as `HEADLINE_GRADIENT`, tuned for dark backgrounds */
export const HEADLINE_GRADIENT_ON_DARK =
  "linear-gradient(90deg,#ffffff 0%,#ffffff 30%,#b8d9f0 38%,#f0b8d4 44%,#f5e6a8 50%,#ffffff 56%,#9ec9ea 62%,#ffffff 68%,transparent 74%,transparent)";

/** Gradient sweep on scroll — same animation as home page section titles. Use `as="h1"` for page heroes. */
export function GradientSectionHeading({
  as = "h2",
  className,
  style,
  children,
}: {
  as?: "h1" | "h2";
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const { ref, inView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const inner = (
    <span
      className={cn("gradient-text-animate inline", inView && "gradient-text-animate--play")}
      style={{ backgroundImage: HEADLINE_GRADIENT }}
    >
      {children}
    </span>
  );
  if (as === "h1") {
    return (
      <h1 ref={ref} className={className} style={style}>
        {inner}
      </h1>
    );
  }
  return (
    <h2 ref={ref} className={className} style={style}>
      {inner}
    </h2>
  );
}
export function GradientSectionHeadingOnDark({
  as = "h2",
  className,
  style,
  children,
}: {
  as?: "h1" | "h2";
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const { ref, inView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const inner = (
    <span
      className={cn("gradient-text-animate inline", inView && "gradient-text-animate--play")}
      style={{ backgroundImage: HEADLINE_GRADIENT_ON_DARK }}
    >
      {children}
    </span>
  );

  if (as === "h1") {
    return (
      <h1 ref={ref} className={className} style={style}>
        {inner}
      </h1>
    );
  }
  return (
    <h2 ref={ref} className={className} style={style}>
      {inner}
    </h2>
  );
}
export function SectionHeadingOnDarkStatic({
  className,
  style,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <h2 className={className} style={style}>
      <span
        className="gradient-text-static-on-dark inline"
        style={{ backgroundImage: HEADLINE_GRADIENT_ON_DARK }}
      >
        {children}
      </span>
    </h2>
  );
}
